/**
 * WebhookRuleStatsTab - Aba de estatísticas para regras de webhook
 * 
 * Exibe cards de métricas e gráfico de volume por hora.
 * Reutiliza o WebhookVolumeChart do CRM.
 * 
 * @version 1.0.0
 * @date 2026-01-30
 */

import { useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Activity, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Percent,
  Zap
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { WebhookVolumeChart } from '@/components/crm/WebhookVolumeChart';
import { useRoutingWebhookLogs } from '@/hooks/useRoutingWebhookLogs';

// ============= INTERFACES =============

interface WebhookRuleStatsTabProps {
  /** ID da regra de roteamento */
  ruleId: string;
}

// ============= COMPONENTE DE CARD DE MÉTRICA =============

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  variant?: 'default' | 'success' | 'error' | 'warning';
}

function StatCard({ icon, label, value, subValue, variant = 'default' }: StatCardProps) {
  const variantStyles = {
    default: 'bg-primary/10 text-primary',
    success: 'bg-green-500/10 text-green-600',
    error: 'bg-red-500/10 text-red-600',
    warning: 'bg-amber-500/10 text-amber-600',
  };

  return (
    <Card className="relative overflow-hidden transition-all duration-300 hover:shadow-md">
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-muted/20" />
      <CardContent className="relative p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-lg ${variantStyles[variant]}`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </p>
            <p className={`font-bold tracking-tight mt-0.5 ${
              typeof value === 'string' && value.length > 8 
                ? 'text-base' 
                : 'text-2xl'
            }`}>
              {value}
            </p>
            {subValue && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {subValue}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============= COMPONENTE PRINCIPAL =============

export function WebhookRuleStatsTab({ ruleId }: WebhookRuleStatsTabProps) {
  // Hook de estatísticas e volume
  const { 
    stats, 
    isLoadingStats, 
    fetchStats,
    volumeData,
    isLoadingVolume,
    fetchVolume
  } = useRoutingWebhookLogs();

  // Carregar dados ao montar
  useEffect(() => {
    fetchStats(ruleId);
    fetchVolume(ruleId, 24);
  }, [ruleId, fetchStats, fetchVolume]);

  // Loading state
  if (isLoadingStats) {
    return (
      <div className="space-y-6">
        {/* Cards skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
        {/* Gráfico skeleton */}
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  // Formatar taxa de sucesso
  const successRate = stats?.successRate 
    ? `${stats.successRate.toFixed(1)}%` 
    : '0%';

  // Formatar última requisição
  const lastRequestText = stats?.lastRequestAt
    ? formatDistanceToNow(new Date(stats.lastRequestAt), { 
        addSuffix: true, 
        locale: ptBR 
      })
    : 'Nunca';

  return (
    <div className="space-y-6">
      {/* Grid de cards de métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total de Requisições */}
        <StatCard
          icon={<Activity className="h-5 w-5" />}
          label="Total de Requisições"
          value={stats?.totalRequests || 0}
          variant="default"
        />

        {/* Requisições com Sucesso */}
        <StatCard
          icon={<CheckCircle2 className="h-5 w-5" />}
          label="Com Sucesso"
          value={stats?.successfulRequests || 0}
          subValue={`${stats?.totalActionsExecuted || 0} ações executadas`}
          variant="success"
        />

        {/* Taxa de Sucesso */}
        <StatCard
          icon={<Percent className="h-5 w-5" />}
          label="Taxa de Sucesso"
          value={successRate}
          subValue={stats?.failedRequests ? `${stats.failedRequests} falha(s)` : undefined}
          variant={
            (stats?.successRate || 0) >= 80 
              ? 'success' 
              : (stats?.successRate || 0) >= 50 
                ? 'warning' 
                : 'error'
          }
        />

        {/* Última Requisição */}
        <StatCard
          icon={<Clock className="h-5 w-5" />}
          label="Última Requisição"
          value={lastRequestText}
          subValue={
            stats?.avgProcessingTime 
              ? `Média: ${stats.avgProcessingTime}ms` 
              : undefined
          }
          variant="default"
        />
      </div>

      {/* Gráfico de volume (reutiliza componente do CRM) */}
      <WebhookVolumeChart 
        data={volumeData} 
        isLoading={isLoadingVolume} 
      />
    </div>
  );
}
